/* ============================================================
   Entitlement — the single answer to "what may this account do?".

   Lives in shared/ for the same reason the recurrence and consistency engines
   do: both sides need it and they must not disagree. The app gates its UI on
   `canWrite`; `summary-dispatch.ts` gates its query on `isEntitled`. One table,
   one place to change it.

   Deliberately a plain function over a plain union, with no dependency on
   RevenueCat, Drizzle or React. The billing VENDOR is an implementation detail
   of how `Plan` gets set, not of what it means.
   ============================================================ */

/** Mirrors `planEnum` in `backend/src/db/schema.ts`. Kept as a literal union
    rather than imported from Drizzle so the app doesn't pull the DB layer in. */
export type Plan = 'none' | 'trialing' | 'pro' | 'expired' | 'comped';

export const PLANS: readonly Plan[] = ['none', 'trialing', 'pro', 'expired', 'comped'] as const;

export function isPlan(value: unknown): value is Plan {
  return typeof value === 'string' && (PLANS as readonly string[]).includes(value);
}

/** WHY a plan is what it is, when the app words it differently. Only one case
    today: `transferred` — the account's subscription moved to another Habishine
    account on the same Apple ID (RevenueCat restore behavior is "transfer";
    docs/subscriptions.md §7c). The plan itself is `expired` either way; the
    reason is what lets the app say "it's on another account" instead of "it
    ended", and offer Restore rather than a new trial. */
export type PlanReason = 'transferred';

export function isPlanReason(value: unknown): value is PlanReason {
  return value === 'transferred';
}

/** WHICH store bills the current plan, for the one thing that genuinely
    differs by store: where to send someone to manage or cancel it. `app_store`
    is Apple IAP, managed from the iOS subscription sheet; `web` is a
    subscription bought at app.habishine.com (RevenueCat Web Billing on Stripe),
    managed from RevenueCat's customer portal. Null for `none`, a comp, and any
    row written before the field existed — a client treats null as "unknown",
    never as a particular store. */
export type PlanStore = 'app_store' | 'web';

export function isPlanStore(value: unknown): value is PlanStore {
  return value === 'app_store' || value === 'web';
}

/* ---------- what each state allows ---------- */

/** Full access: read AND write. The dispatcher also uses this to decide whether
    an account still gets digests — a lapsed user should stop receiving push,
    since that is the one surface the server drives with no client to gate. */
export function isEntitled(plan: Plan): boolean {
  return plan === 'trialing' || plan === 'pro' || plan === 'comped';
}

/** May create, edit, complete or check in. Identical to `isEntitled` today, and
    separate from it ON PURPOSE: a future free tier would be entitled to *use*
    the app while still being capped somewhere, and that distinction wants a
    seam rather than a find-and-replace. */
export function canWrite(plan: Plan): boolean {
  return isEntitled(plan);
}

/** May open the app at all and see their own data.

    `expired` is TRUE — a lapsed subscriber keeps read access, which is the §6
    decision: their data stays visible, writing stops, and account deletion stays
    reachable for Guideline 5.1.1(v). Withholding someone's own history is the
    punitive framing this product rejects everywhere else, and a hard wall
    converts worse than a visible one.

    `none` is FALSE — an account that has never subscribed meets the paywall.
    There is no free tier in 1.x. */
export function canRead(plan: Plan): boolean {
  return plan !== 'none';
}

/** Whether to show the paywall as an unavoidable gate (`none`) rather than as a
    banner over readable content (`expired`). */
export function needsHardPaywall(plan: Plan): boolean {
  return plan === 'none';
}

/* ---------- expiry ---------- */

/** Resolve the stored plan against the clock.

    A webhook can be late, retried, or lost; `planExpiresAt` is the store's own
    paid-through date and does not depend on delivery. So an entitled plan whose
    expiry has passed reads as `expired` without waiting to be told. The failure
    mode this removes is "subscription ended, webhook never arrived, account has
    Pro forever".

    `comped` never expires and is returned untouched — that is the point of it
    being a distinct value (§8: the App Review account). */
export function effectivePlan(plan: Plan, expiresAt: Date | string | null | undefined, now: Date = new Date()): Plan {
  if (plan === 'comped' || plan === 'none' || plan === 'expired') return plan;
  if (!expiresAt) return plan;
  const end = expiresAt instanceof Date ? expiresAt : new Date(expiresAt);
  if (Number.isNaN(end.getTime())) return plan;
  return end.getTime() <= now.getTime() ? 'expired' : plan;
}

/* ---------- offline grace (client only, but shared for testability) ---------- */

/** How long a cached entitlement survives with no successful sync.

    §5: an entitlement that CANNOT BE DETERMINED resolves to granted, never
    denied. The client is offline-first and the network is an eventual mirror
    (CLAUDE.md); locking someone out of their own habits because a billing
    server was unreachable is a one-star review with a screenshot attached.

    Seven days is long enough to cover a holiday with no signal and short enough
    that a cancelled subscription does not linger indefinitely. ⚠️ It also means
    a determined user gets free Pro by staying offline. That is the accepted
    trade, made deliberately rather than discovered. */
export const OFFLINE_GRACE_DAYS = 7;

/** Whether a cached entitlement is still trusted, given when it was last
    confirmed by a successful sync. */
export function cacheWithinGrace(confirmedAt: Date | string | null | undefined, now: Date = new Date()): boolean {
  if (!confirmedAt) return false;
  const at = confirmedAt instanceof Date ? confirmedAt : new Date(confirmedAt);
  if (Number.isNaN(at.getTime())) return false;
  const ageMs = now.getTime() - at.getTime();
  return ageMs >= 0 && ageMs <= OFFLINE_GRACE_DAYS * 24 * 60 * 60 * 1000;
}
